import * as React from 'react'
import map from 'lodash/map'
import some from 'lodash/some'
import {exhaustive} from '@freckle/exhaustive'
import {type PolyfilledEvent} from '@frontrow/console/src/polyfills'
import {cn} from '@frontrow/console/src/lib/utils'
import {SELECT_BASE} from '@frontrow/console/src/lib/form-styles'

export type SelectorOptionT<T> = {
  tag: 'option'
  key: T
  disabled: boolean
  label: string
  dataTest?: string
}

type SelectorGroupT<T> = {
  tag: 'group'
  key: string
  label: string
  options: Array<SelectorOptionT<T>>
}

export type SelectorItemT<T> = SelectorOptionT<T> | SelectorGroupT<T>

export function createSelectorGroupAsItem<T>(
  key: string,
  label: string,
  options: Array<SelectorOptionT<T>>
): SelectorItemT<T> {
  return {tag: 'group', key, label, options}
}

export function createSelectorOptionAsItem<T>(
  key: T,
  label: string,
  disabled: boolean = false,
  dataTest: string | undefined | null = null
): SelectorItemT<T> {
  const dataTestAttr = dataTest === null || dataTest === undefined ? {} : {dataTest}
  return {tag: 'option', key, disabled, label, ...dataTestAttr}
}

export function createSelectorOption<T>(
  key: T,
  label: string,
  disabled: boolean = false,
  dataTest: string | undefined | null = null
): SelectorOptionT<T> {
  const dataTestAttr = dataTest === null || dataTest === undefined ? {} : {dataTest}
  return {tag: 'option', key, disabled, label, ...dataTestAttr}
}

type GenericSelectorProps<T> = {
  onChangeSelection: (selection: T) => void
  selection: T | undefined | null
  collection: Array<SelectorItemT<T>>
  disabled?: boolean
  required?: boolean
  className?: string
  id?: string
  dataTest?: string
}

export function GenericSelector<T>(props: GenericSelectorProps<T>): React.ReactElement {
  function onChange(e: PolyfilledEvent) {
    // Need this refinement to access `e.target.value`
    if (e.target instanceof HTMLSelectElement) {
      props.onChangeSelection(JSON.parse(e.target.value))
    }
  }

  function renderOption(option: SelectorOptionT<T>): React.ReactElement {
    const key = JSON.stringify(option.key)
    const {dataTest, disabled, label} = option
    const dataTestAttr = dataTest === null || dataTest === undefined ? {} : {'data-test': dataTest}
    return (
      <option key={key} disabled={disabled} value={key} {...dataTestAttr}>
        {label}
      </option>
    )
  }

  const options = map(props.collection, item => {
    switch (item.tag) {
      case 'group':
        return (
          <optgroup key={item.key} label={item.label}>
            {map(item.options, renderOption)}
          </optgroup>
        )
      case 'option':
        return renderOption(item)
      default:
        return exhaustive(item as never, 'SelectorItemT')
    }
  })

  const hasNullKey = some(props.collection, item => {
    switch (item.tag) {
      case 'group':
        return some(item.options, option => option.key === null)
      case 'option':
        return item.key === null
      default:
        return exhaustive(item as never, 'SelectorItemT')
    }
  })

  // A value of undefined will show the select box with no selected option (nothing highlighted).
  // In the case of an item with a `null` key, the logic to use `undefined` should be bypassed
  // because a selection of `null` should indicate that the item in question is selected.
  const value =
    hasNullKey === false && (props.selection === null || props.selection === undefined)
      ? undefined
      : JSON.stringify(props.selection)

  return (
    <select
      id={props.id}
      className={cn(SELECT_BASE, props.className)}
      // eslint-disable-next-line react/jsx-no-bind
      onChange={onChange}
      disabled={props.disabled}
      required={props.required}
      value={value}
      data-test={props.dataTest}
    >
      {options}
    </select>
  )
}
